import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PortalReminderRow, PortalDigest } from '../api'
import { getNotifications, setReminderMuted, setWeeklyDigest } from '../api'

const NEXT_LABEL: Record<PortalReminderRow['kind'], string> = { call: 'Next call', program: 'Next session', webinar: 'Starts' }
const ACCESS_NOTE: Record<PortalReminderRow['kind'], string> = {
  call: 'You still have access to join the call.',
  program: 'You still have access to all the sessions.',
  webinar: 'You are still registered and can join as usual.',
}

function formatNext(kind: PortalReminderRow['kind'], iso: string | null): string {
  if (!iso) return 'Nothing scheduled right now'
  return NEXT_LABEL[kind] + ' ' + new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

export default function Notifications() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<PortalReminderRow[] | null>(null)
  const [err, setErr] = useState('')
  const [busyRef, setBusyRef] = useState<string | null>(null)
  const [digest, setDigest] = useState<PortalDigest | null>(null)
  const [digestBusy, setDigestBusy] = useState(false)

  useEffect(() => {
    getNotifications()
      .then(r => { setRows(r.reminders); setDigest(r.digest) })
      .catch(() => setErr('We could not load your reminders. Please try again in a moment.'))
  }, [])

  async function toggle(row: PortalReminderRow) {
    setBusyRef(`${row.kind}:${row.ref}`)
    setErr('')
    try {
      // The server answers with the fresh list, so what we show is what it saved.
      const r = await setReminderMuted(row.kind, row.ref, !row.muted)
      setRows(r.reminders)
    } catch {
      setErr('That change did not save. Please try again.')
    } finally {
      setBusyRef(null)
    }
  }

  async function chooseDigest(choice: { mode: PortalDigest['mode']; edition?: string }) {
    setDigestBusy(true)
    setErr('')
    try {
      // What we show is what the server saved.
      const r = await setWeeklyDigest(choice)
      setDigest(r.digest)
    } catch {
      setErr('That change did not save. Please try again.')
    } finally {
      setDigestBusy(false)
    }
  }

  return (
    <div className="p-page">
      <button className="p-roadmap-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
      <div className="p-dash-header">
        <div className="p-dash-eyebrow">Your settings</div>
        <h1 className="p-dash-heading">Notifications</h1>
        <p className="p-dash-sub">Choose which reminders you would like to receive for your calls, programs and webinars.</p>
      </div>

      {err && <div className="p-error">{err}</div>}

      <div className="p-section-label">Reminders</div>
      {rows === null && !err && <div className="p-spinner" />}
      {rows !== null && rows.length === 0 && (
        <p className="p-dash-sub">You are not on any reminders right now.</p>
      )}
      <div className="p-notif-list">
        {rows?.map(row => (
          <div className="p-notif-row" key={`${row.kind}:${row.ref}`}>
            <div>
              <div className="p-notif-label">{row.label}</div>
              <div className="p-notif-sub">
                {row.muted
                  ? `Reminders are off. ${ACCESS_NOTE[row.kind]}`
                  : formatNext(row.kind, row.nextAt)}
              </div>
            </div>
            <button
              className={`p-btn p-btn-sm ${row.muted ? 'p-btn-primary' : 'p-btn-outline'}`}
              disabled={busyRef === `${row.kind}:${row.ref}`}
              onClick={() => void toggle(row)}
            >
              {busyRef === `${row.kind}:${row.ref}` ? '…' : row.muted ? 'Turn reminders on' : 'Turn reminders off'}
            </button>
          </div>
        ))}
      </div>

      {digest && (
        <>
          <div className="p-section-label" style={{ marginTop: 32 }}>Your weekly calls roundup</div>
          <div className="p-notif-row" data-slot="weekly-digest" style={{ display: 'block' }}>
            <div className="p-notif-sub" style={{ marginBottom: 8 }}>
              Every Monday, the calls you are on this week. Turning this off never removes you from any call.
              Reminders for each call are separate, above.
            </div>
            <fieldset disabled={digestBusy} style={{ border: 0, padding: 0, margin: 0 }}>
              {([
                { id: 'regional', label: `The calls in friendly hours where I am — ${digest.placed.name} (we think that is you)`, mode: 'regional' },
                ...digest.editions.map(e => ({ id: `edition:${e.key}`, label: e.name, mode: 'edition', edition: e.key })),
                { id: 'global', label: 'Everything, globally', mode: 'global' },
                { id: 'off', label: 'Off', mode: 'off' },
              ] as Array<{ id: string; label: string; mode: PortalDigest['mode']; edition?: string }>).map(o => {
                const current = digest.mode === 'edition' ? `edition:${digest.edition}` : digest.mode
                return (
                  <label key={o.id} className="p-notif-label" style={{ display: 'flex', gap: 10, padding: '6px 0', cursor: 'pointer', fontWeight: 400 }}>
                    <input
                      type="radio"
                      name="calls-digest"
                      checked={current === o.id}
                      onChange={() => void chooseDigest({ mode: o.mode, edition: o.edition })}
                    />
                    <span>{o.label}</span>
                  </label>
                )
              })}
            </fieldset>
          </div>
        </>
      )}
    </div>
  )
}
